/**
 * recentBackgroundAudio.js — IndexedDB-backed cache of recent background-audio
 * uploads.
 *
 * Persists the last N uploaded background-audio Files (as Blobs + metadata)
 * across browser sessions, so the user can switch between favourites without
 * re-uploading. Browsers reliably store Blobs in IndexedDB up to several
 * hundred MB before quota pressure — comfortably enough for 5 audio files.
 *
 * Pinning: any entry can be pinned. Pinned entries are immune to the
 * MAX_ENTRIES eviction rule and always sort before non-pinned entries in
 * getRecent()/getRecentMeta() output.
 *
 * No external dep. ~120-line wrapper around the native IndexedDB API.
 *
 * Schema:
 *   db `neurowave_audio` v1
 *     store `recents` (keyPath='id') = { id, name, size, type, addedAt, pinned, blob }
 *
 * Public API:
 *   addRecent(file)        → Promise<entry>
 *   getRecent()            → Promise<entry[]>   (pinned first by pinOrder, then newest)
 *   getRecentMeta()        → Promise<entry[]>   (no blob — cheap)
 *   togglePin(id)          → Promise<boolean>   (assigns pinOrder on pin)
 *   reorderPinned(ids)     → Promise<void>      (drag-reorder result)
 *   removeRecent(id)       → Promise<void>
 *   clearUnpinned()        → Promise<void>      (wipes non-pinned; pins survive)
 *   clearRecent()          → Promise<void>
 */

const DB_NAME = 'neurowave_audio';
const STORE = 'recents';
const VERSION = 1;
const MAX_ENTRIES = 5;

let _dbPromise = null;

function _openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('addedAt', 'addedAt');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function _txStore(mode) {
  return _openDb().then((db) => {
    const tx = db.transaction(STORE, mode);
    return tx.objectStore(STORE);
  });
}

/** Stable id derived from the file's content-identifying fields. Means the
 *  same file dropped twice doesn't create two entries — it just refreshes. */
function _fileId(file) {
  return `${file.name}::${file.size}::${file.lastModified || 0}`;
}

/**
 * Add or refresh an entry for `file`. Preserves the `pinned` flag and the
 * user-controlled `pinOrder` when an existing entry with the same id is being
 * refreshed. Trims oldest non-pinned entries beyond MAX_ENTRIES; pinned
 * entries are never evicted.
 */
export async function addRecent(file) {
  if (!file) throw new Error('addRecent: file required');
  const id = _fileId(file);

  // Check for an existing entry so we can preserve its pinned flag + order.
  const readStore = await _txStore('readonly');
  const existing = await _wrap(readStore.get(id));

  const entry = {
    id,
    name: file.name,
    size: file.size,
    type: file.type || 'audio/*',
    addedAt: Date.now(),
    pinned: existing?.pinned === true,
    pinOrder: typeof existing?.pinOrder === 'number' ? existing.pinOrder : null,
    blob: file, // File extends Blob, IDB stores it as a Blob
  };

  const store = await _txStore('readwrite');
  await _wrap(store.put(entry));

  // Trim — only evict non-pinned entries. Pinned items are immune to the cap.
  const all = await _wrap(store.index('addedAt').getAll());
  const nonPinned = all.filter((e) => !e.pinned);
  if (nonPinned.length > MAX_ENTRIES) {
    const overflow = nonPinned
      .sort((a, b) => a.addedAt - b.addedAt)
      .slice(0, nonPinned.length - MAX_ENTRIES);
    const tx = (await _openDb()).transaction(STORE, 'readwrite');
    overflow.forEach((e) => tx.objectStore(STORE).delete(e.id));
  }

  const { blob: _b, ...meta } = entry;
  return meta;
}

/**
 * All entries. Sort order:
 *   1. Pinned entries first, by user-chosen `pinOrder` ascending (stable)
 *      — entries without a pinOrder fall to the end of the pinned group by addedAt.
 *   2. Non-pinned entries after, newest-first by addedAt.
 */
export async function getRecent() {
  const store = await _txStore('readonly');
  const all = await _wrap(store.getAll());
  return all.sort((a, b) => {
    if (!!b.pinned - !!a.pinned) return !!b.pinned - !!a.pinned;
    if (a.pinned && b.pinned) {
      const ao = typeof a.pinOrder === 'number' ? a.pinOrder : Number.POSITIVE_INFINITY;
      const bo = typeof b.pinOrder === 'number' ? b.pinOrder : Number.POSITIVE_INFINITY;
      if (ao !== bo) return ao - bo;
      return b.addedAt - a.addedAt; // tiebreaker
    }
    return b.addedAt - a.addedAt;
  });
}

/** Metadata-only listing (no blob). Cheap; safe to call on every render. */
export async function getRecentMeta() {
  const all = await getRecent();
  return all.map(({ blob: _b, ...meta }) => meta);
}

/**
 * Flip the pinned flag on an entry. When pinning, assigns the next available
 * pinOrder so the entry lands at the END of the pinned row (like a queue).
 * When unpinning, clears pinOrder.
 */
export async function togglePin(id) {
  const readStore = await _txStore('readonly');
  const existing = await _wrap(readStore.get(id));
  if (!existing) throw new Error(`togglePin: no entry with id=${id}`);

  const nowPinned = !existing.pinned;
  let pinOrder = null;
  if (nowPinned) {
    const all = await _wrap(readStore.getAll());
    const maxOrder = all
      .filter((e) => e.pinned && typeof e.pinOrder === 'number')
      .reduce((m, e) => Math.max(m, e.pinOrder), -1);
    pinOrder = maxOrder + 1;
  }

  const updated = { ...existing, pinned: nowPinned, pinOrder };
  const writeStore = await _txStore('readwrite');
  await _wrap(writeStore.put(updated));
  return nowPinned;
}

/**
 * Rewrite pinOrder for every id in `orderedIds` (index 0 = first). Non-listed
 * ids keep their existing pinOrder. Any unknown id is silently ignored.
 * Use to apply a drag-reorder across the pinned row.
 */
export async function reorderPinned(orderedIds) {
  if (!Array.isArray(orderedIds) || orderedIds.length === 0) return;
  const store = await _txStore('readwrite');
  for (let i = 0; i < orderedIds.length; i++) {
    const id = orderedIds[i];
    const existing = await _wrap(store.get(id));
    if (!existing || !existing.pinned) continue;
    const updated = { ...existing, pinOrder: i };
    await _wrap(store.put(updated));
  }
}

/** Delete every non-pinned entry. Pinned entries survive. */
export async function clearUnpinned() {
  const store = await _txStore('readwrite');
  const all = await _wrap(store.getAll());
  const tx = (await _openDb()).transaction(STORE, 'readwrite');
  all.filter((e) => !e.pinned).forEach((e) => tx.objectStore(STORE).delete(e.id));
  // Wait for transaction completion.
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function removeRecent(id) {
  const store = await _txStore('readwrite');
  await _wrap(store.delete(id));
}

export async function clearRecent() {
  const store = await _txStore('readwrite');
  await _wrap(store.clear());
}

/** Convert an IDBRequest to a Promise. */
function _wrap(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
